import { useEffect, useState } from 'react';
import {
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { isEmpty, isNil, map } from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
  deleteExternalTransferPharmacy,
  listExternalPharmacies,
} from 'store/thunks/pharmacyThunks';
import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import { casingFormat } from 'common/utils';
import useRoles from 'common/hooks/useRoles';
import useInfoDialog from 'common/hooks/useInfoDialog';
import roles from 'common/constants/roles';
import EditExternalTransferPharmacyDialog from './EditExternalTransferPharmacyDialog';

const headCells = [
  { id: 'pharmacyName', label: 'Pharmacy Name', align: 'left' },
  { id: 'address', label: 'Address', align: 'left' },
  { id: 'phone', label: 'Phone', align: 'left' },
  { id: 'fax', label: 'Fax', align: 'left' },
  { id: 'npi', label: 'NPI', align: 'left' },
  { id: 'dea', label: 'DEA', align: 'left' },
  { id: 'createdTimestamp', label: 'Created Date', align: 'left' },
  { id: 'modifiedTimestamp', label: 'Modified Date', align: 'left' },
  { id: 'actions', label: 'Actions', align: 'right' },
];

const ExternalPharmaciesTableRow = ({ pharmacy, isAdmin }) => {
  const dispatch = useDispatch();
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const menuOpen = !!anchorEl;

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleShowEditDialog = () => {
    setShowEditDialog(true);
    handleMenuClose();
  };

  const handleDeletePharmacy = () => {
    handleMenuClose();
    showInfoDialog({
      title: 'Confirm Delete',
      message: 'Are you sure you want to delete this external transfer pharmacy?',
      callback: () => dispatch(deleteExternalTransferPharmacy(pharmacy.pharmacyId)),
      cancelable: true,
      isWarning: true,
      confirmButtonText: 'Confirm',
    });
  };

  const handleCloseEditDialog = () => setShowEditDialog(false);

  const formatAddress = (pharmacyData) => {
    const parts = [
      pharmacyData.addressLine1,
      pharmacyData.addressLine2,
      `${pharmacyData.city}, ${pharmacyData.state} ${pharmacyData.zip}`,
    ].filter(Boolean);
    return parts.join(', ');
  };

  return (
    <>
      <InfoDialog />
      <EditExternalTransferPharmacyDialog
        open={showEditDialog}
        onClose={handleCloseEditDialog}
        pharmacy={pharmacy}
      />
      <TableCell align='left'>{casingFormat(pharmacy.pharmacyName)}</TableCell>
      <TableCell align='left'>{formatAddress(pharmacy)}</TableCell>
      <TableCell align='left'>{pharmacy.phone || 'N/A'}</TableCell>
      <TableCell align='left'>{pharmacy.fax || 'N/A'}</TableCell>
      <TableCell align='left'>{pharmacy.npi || 'N/A'}</TableCell>
      <TableCell align='left'>{pharmacy.dea || 'N/A'}</TableCell>
      <TableCell align='left'>
        {pharmacy.createdTimestamp
          ? format(new Date(pharmacy.createdTimestamp), 'MM/dd/yyyy')
          : 'N/A'}
      </TableCell>
      <TableCell align='left'>
        {pharmacy.modifiedTimestamp
          ? format(new Date(pharmacy.modifiedTimestamp), 'MM/dd/yyyy')
          : 'N/A'}
      </TableCell>
      {isAdmin && (
        <TableCell align='right'>
          <IconButton
            id={`ExternalTransferPharmacy-Table-${pharmacy.pharmacyId}options-button`}
            aria-label={`Pharmacy options ${pharmacy.pharmacyId}`}
            aria-controls={
              menuOpen
                ? `ExternalTransferPharmacy-Table-${pharmacy.pharmacyId}options-menu`
                : undefined
            }
            aria-haspopup='true'
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={handleMenuOpen}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id={`ExternalTransferPharmacy-Table-${pharmacy.pharmacyId}options-menu`}
            aria-labelledby={`ExternalTransferPharmacy-Table-${pharmacy.pharmacyId}options-button`}
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={handleShowEditDialog}>Edit</MenuItem>
            <MenuItem onClick={handleDeletePharmacy}>Delete</MenuItem>
          </Menu>
        </TableCell>
      )}
    </>
  );
};

ExternalPharmaciesTableRow.propTypes = {
  pharmacy: PropTypes.shape({
    pharmacyId: PropTypes.string.isRequired,
    pharmacyName: PropTypes.string,
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    phone: PropTypes.string,
    fax: PropTypes.string,
    npi: PropTypes.string,
    dea: PropTypes.string,
    pharmacistName: PropTypes.string,
    pharmacistNpi: PropTypes.string,
    createdTimestamp: PropTypes.string,
    modifiedTimestamp: PropTypes.string,
  }).isRequired,
  isAdmin: PropTypes.bool.isRequired,
};

const ExternalPharmaciesTable = () => {
  const dispatch = useDispatch();
  const hasAccess = useRoles();
  const isAdmin = hasAccess.hasAuthorizedRoles([roles.admin]);

  const { pharmacyList, loading } = useSelector(
    ({ systemConfigs }) => systemConfigs.externalPharmacy
  );

  useEffect(() => {
    dispatch(listExternalPharmacies());
  }, [dispatch]);

  const visibleHeadCells = isAdmin ? headCells : headCells.filter((cell) => cell.id !== 'actions');

  return (
    <Grid component={Paper} container direction='column' sx={{ py: 1, px: 2 }}>
      <Grid item>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {visibleHeadCells.map((headCell) => (
                  <TableCell key={headCell.id} align={headCell.align}>
                    {headCell.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {pharmacyList &&
                map(pharmacyList, (pharmacy) => (
                  <TableRow key={pharmacy.pharmacyId} hover>
                    <ExternalPharmaciesTableRow pharmacy={pharmacy} isAdmin={isAdmin} />
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {(isNil(pharmacyList) || loading) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(pharmacyList) && !loading && isEmpty(pharmacyList) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

export default ExternalPharmaciesTable;
